import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Pencil, Trash } from '@phosphor-icons/react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function TableShapeIcon({ shape, sx = {} }) {
  const shapeStyle = {
    round: { borderRadius: '50%', width: 24, height: 24 },
    square: { borderRadius: 0, width: 24, height: 24 },
    rectangular: { borderRadius: 0, width: 32, height: 20 },
    rectangle: { borderRadius: 0, width: 32, height: 20 }
  };
  const style = shapeStyle[shape] || shapeStyle.round;
  return (
    <Box
      sx={{
        ...style,
        border: '2px solid',
        borderColor: 'primary.main',
        flexShrink: 0,
        ...sx
      }}
    />
  );
}

function SeatingPlan() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [weddingDetails, setWeddingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openTableDialog, setOpenTableDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableForm, setTableForm] = useState({
    name: '',
    capacity: 8,
    shape: 'round',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tablesRes, guestsRes, detailsRes] = await Promise.all([
        api.get('/api/seating-plan'),
        api.get('/api/guests'),
        api.get('/api/wedding-details').catch(() => ({ data: null }))
      ]);
      
      const seatingPlan = tablesRes.data;
      setTables(seatingPlan?.tables || []);
      setWeddingDetails(detailsRes?.data || null);

      // Confirmed guests only (full list for seating plan data)
      const confirmedGuests = Array.isArray(guestsRes.data)
        ? guestsRes.data.filter(g => g.status === 'confirmed')
        : [];
      setGuests(confirmedGuests);
    } catch (err) {
      setError('Failed to load seating plan');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTable = () => {
    setEditingTable(null);
    setTableForm({ name: '', capacity: 8, shape: 'round' });
    setOpenTableDialog(true);
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableForm({
      name: table.name,
      capacity: table.seats || table.capacity || 8, // Backend uses 'seats', frontend uses 'capacity'
      shape: table.shape || 'round',
    });
    setOpenTableDialog(true);
  };

  const handleDeleteTable = async (tableId) => {
    try {
      const response = await api.delete(`/api/seating-plan/tables/${tableId}`);
      // Backend returns the full seating plan object with tables array (transformed)
      setTables(response.data?.tables || []);
    } catch (error) {
      setError('Failed to delete table');
    }
  };

  const handleSubmitTable = async () => {
    try {
      // Backend expects { name, shape, seats } not { name, capacity, shape }
      const tableData = {
        name: tableForm.name,
        shape: tableForm.shape,
        seats: tableForm.capacity
      };

      let response;
      if (editingTable) {
        response = await api.put(`/api/seating-plan/tables/${editingTable._id}`, tableData);
      } else {
        response = await api.post('/api/seating-plan/tables', tableData);
      }
      
      // Backend returns the full seating plan object with tables array (transformed)
      setTables(response.data?.tables || []);
      setOpenTableDialog(false);
    } catch (error) {
      setError('Failed to save table');
    }
  };

  const handleDragEnd = async (result) => {

    if (!result.destination || !tables || !Array.isArray(tables)) return;

    const { source, destination } = result;
    
    // Get the guest being moved
    const unseatedGuests = getUnseatedGuests();
    let guestId;
    
    if (source.droppableId === 'unseated') {
      // Moving from unseated guests
      const guest = unseatedGuests[source.index];
      if (!guest) return;
      guestId = guest._id?.toString() || guest._id;
    } else {
      // Moving from a table
      const sourceTable = tables.find(t => {
        const tableId = t._id?.toString() || t._id;
        return tableId === source.droppableId || t._id === source.droppableId;
      });
      if (!sourceTable) return;
      guestId = sourceTable.guestIds?.[source.index];
      if (!guestId) return;
      guestId = guestId.toString();
    }
    
    // Moving within same table or to different table
    if (source.droppableId === destination.droppableId) {
      // Reorder within same table (not from unseated)
      if (source.droppableId === 'unseated') {
        // No-op: reordering unseated guests doesn't need server update
        return;
      }
      
      const table = tables.find(t => {
        const tableId = t._id?.toString() || t._id;
        return tableId === source.droppableId || t._id === source.droppableId;
      });
      if (!table) return;
      
      const newGuestIds = Array.from(table.guestIds || []);
      const [removed] = newGuestIds.splice(source.index, 1);
      newGuestIds.splice(destination.index, 0, removed);
      
      const updatedTable = { ...table, guestIds: newGuestIds };
      const tableIdStr = table._id?.toString();
      setTables(tables.map(t => {
        const tIdStr = t._id?.toString();
        return (tIdStr === tableIdStr || t._id === table._id) ? updatedTable : t;
      }));
      
      // Update on server
      await api.put(`/api/seating-plan/tables/${table._id}/guests`, { guestIds: newGuestIds });
      
      // Refresh data from server
      const updatedResponse = await api.get('/api/seating-plan');
      setTables(updatedResponse.data?.tables || []);
    } else {
      // Move between tables or from/to unseated
      if (destination.droppableId === 'unseated') {
        // Moving to unseated (remove from table)
        const sourceTable = tables.find(t => {
          const tableId = t._id?.toString() || t._id;
          return tableId === source.droppableId || t._id === source.droppableId;
        });
        if (!sourceTable) return;
        
        const sourceGuestIds = Array.from(sourceTable.guestIds || []).filter(id => id.toString() !== guestId);
        const sourceIdStr = sourceTable._id?.toString();
        const newTables = tables.map(t => {
          const tIdStr = t._id?.toString();
          return (tIdStr === sourceIdStr || t._id === sourceTable._id) ? { ...t, guestIds: sourceGuestIds } : t;
        });
        
        setTables(newTables);
        
        // Update on server
        await api.put(`/api/seating-plan/tables/${sourceTable._id}/guests`, { guestIds: sourceGuestIds });
        
        // Refresh data from server
        const updatedResponse = await api.get('/api/seating-plan');
        setTables(updatedResponse.data?.tables || []);
      } else {
        // Moving between tables or from unseated to table
        const destTable = tables.find(t => {
          const tableId = t._id?.toString() || t._id;
          return tableId === destination.droppableId || t._id === destination.droppableId;
        });
        if (!destTable) return;
        
        let sourceGuestIds = [];
        let sourceTable = null;
        
        if (source.droppableId !== 'unseated') {
          // Moving from one table to another
          sourceTable = tables.find(t => {
            const tableId = t._id?.toString() || t._id;
            return tableId === source.droppableId || t._id === source.droppableId;
          });
          if (!sourceTable) return;
          sourceGuestIds = Array.from(sourceTable.guestIds || []).filter(id => id.toString() !== guestId);
        }
        
        const destGuestIds = Array.from(destTable.guestIds || []);
        destGuestIds.splice(destination.index, 0, guestId);
        
        const destIdStr = destTable._id?.toString();
        const newTables = tables.map(t => {
          const tIdStr = t._id?.toString();
          if (sourceTable && (tIdStr === sourceTable._id?.toString() || t._id === sourceTable._id)) {
            return { ...t, guestIds: sourceGuestIds };
          }
          if (tIdStr === destIdStr || t._id === destTable._id) {
            return { ...t, guestIds: destGuestIds };
          }
          return t;
        });
        
        setTables(newTables);
        
        // Update on server - only need to update destination table
        // The backend automatically removes guests from other tables when updating
        await api.put(`/api/seating-plan/tables/${destTable._id}/guests`, { guestIds: destGuestIds });
        
        // Refresh data from server
        const updatedResponse = await api.get('/api/seating-plan');
        setTables(updatedResponse.data?.tables || []);
      }
    }
  };

  // Seating plan is for reception: only guests who are yes to reception or evening
  const receptionGuests = useMemo(() => {
    return guests.filter(
      g => g.invitedToReception !== false || g.invitedToEvening === true
    );
  }, [guests]);

  const receptionCapacity =
    weddingDetails?.venues?.reception?.capacity;

  const getCapacityColor = (count, capacity) => {
    if (!capacity) return 'default';
    const percentage = (count / capacity) * 100;
    if (percentage >= 100) return 'error';
    if (percentage >= 85) return 'warning';
    return 'success';
  };

  const getUnseatedGuests = () => {
    if (!tables || !Array.isArray(tables)) return receptionGuests;
    const seatedGuestIds = tables.flatMap(t => (t.guestIds || []).map(id => id.toString()));
    return receptionGuests.filter(g => {
      const guestId = g._id?.toString() || g._id;
      return guestId && !seatedGuestIds.includes(guestId);
    });
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={styles.patterns.pageHeader}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Seating Plan
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTable}
        >
          Add Table
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Unseated Guests */}
        <Card sx={{ ...styles.card.default, mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ ...styles.text.sectionTitle, mb: 0 }}>
                Unseated Guests ({getUnseatedGuests().length})
              </Typography>
              {receptionCapacity != null && (
                <Box sx={{ ml: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Capacity:
                    </Typography>
                    <Chip
                      label={`${receptionGuests.length} / ${receptionCapacity}`}
                      size="small"
                      color={getCapacityColor(receptionGuests.length, receptionCapacity)}
                      variant="outlined"
                    />
                  </Box>
                  {receptionCapacity === 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 0.5, fontWeight: 600 }}
                      onClick={() => navigate('/wedding-details?section=venues&highlight=receptionCapacity')}
                    >
                      Update capacity
                    </Button>
                  )}
                </Box>
              )}
              {receptionCapacity == null && (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Capacity: {receptionGuests.length} / No capacity set
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600 }}
                    onClick={() => navigate('/wedding-details?section=venues&highlight=receptionCapacity')}
                  >
                    Update capacity
                  </Button>
                </Box>
              )}
            </Box>
            <Droppable droppableId="unseated" direction="horizontal">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    ...styles.layout.flexRow,
                    flexWrap: 'wrap',
                    gap: 1,
                    minHeight: 60,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  {getUnseatedGuests().map((guest, index) => (
                    <Draggable key={guest._id} draggableId={guest._id?.toString() || guest._id} index={index}>
                      {(provided, snapshot) => (
                        <Chip
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          label={guest.name}
                          color="primary"
                          sx={{
                            cursor: 'grab',
                            opacity: snapshot.isDragging ? 0.5 : 1,
                          }}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </CardContent>
        </Card>

        {/* Tables Grid */}
        <Grid container spacing={3}>
          {tables && Array.isArray(tables) && tables.map((table) => (
            <Grid item xs={12} sm={6} md={4} key={table._id}>
              <Card sx={styles.card.elevated}>
                <CardContent>
                  <Box sx={{ ...styles.layout.flexBetween, mb: 2 }}>
                    <Box sx={styles.layout.flexRow} gap={1}>
                      <TableShapeIcon shape={table.shape || 'round'} />
                      <Typography variant="h6">
                        {table.name}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => handleEditTable(table)}>
                        <Pencil size={18} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteTable(table._id)}>
                        <Trash size={18} />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {table.guestIds?.length || 0} / {table.capacity} guests
                  </Typography>

                  <Droppable droppableId={table._id?.toString() || table._id}>
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: 100,
                          p: 2,
                          bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'grey.50',
                          border: '2px dashed',
                          borderColor: snapshot.isDraggingOver ? 'primary.main' : 'grey.300',
                          transition: 'all 0.2s',
                        }}
                      >
                        <Box sx={{ ...styles.layout.flexColumn, gap: 1 }}>
                          {table.guestIds?.map((guestId, index) => {
                            // Compare IDs as strings to handle ObjectId vs string mismatches
                            const guestIdStr = guestId?.toString();
                            const guest = guests.find(g => {
                              const gId = g._id?.toString() || g._id;
                              return gId === guestIdStr;
                            });
                            if (!guest) return null;
                            
                            return (
                              <Draggable key={guest._id} draggableId={guest._id?.toString() || guest._id} index={index}>
                                {(provided, snapshot) => (
                                  <Chip
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    label={guest.name}
                                    size="small"
                                    sx={{
                                      cursor: 'grab',
                                      opacity: snapshot.isDragging ? 0.5 : 1,
                                    }}
                                  />
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </Box>
                      </Paper>
                    )}
                  </Droppable>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DragDropContext>

      {/* Add/Edit Table Dialog */}
      <Dialog open={openTableDialog} onClose={() => setOpenTableDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTable ? 'Edit Table' : 'Add New Table'}
        </DialogTitle>
        <DialogContent>
          <Box sx={styles.patterns.formContainer} mt={2}>
            <TextField
              fullWidth
              label="Table Name"
              value={tableForm.name}
              onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
              placeholder="e.g., Table 1, Head Table"
            />
            <TextField
              fullWidth
              label="Capacity"
              type="number"
              value={tableForm.capacity}
              onChange={(e) => setTableForm({ ...tableForm, capacity: parseInt(e.target.value) })}
            />
            <FormControl fullWidth>
              <InputLabel>Shape</InputLabel>
              <Select
                value={tableForm.shape}
                onChange={(e) => setTableForm({ ...tableForm, shape: e.target.value })}
                label="Shape"
              >
                <MenuItem value="round">Round</MenuItem>
                <MenuItem value="rectangular">Rectangle</MenuItem>
                <MenuItem value="square">Square</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTableDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitTable} variant="contained">
            {editingTable ? 'Update' : 'Add'} Table
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SeatingPlan; 